import Ajv, { ErrorObject } from 'ajv';
import { LocationDescriptor } from 'history';
import noop from 'lodash/noop';
import { quickActionSchema as jsonSchema } from './schemas';
import { ITenant } from '../../domains/inventory';
import {
  generateRoleDataMemoised as generateUserRoleData,
  redirect,
} from './utils';
import { IViewModel, IQuickActionInput } from './types';
import { Environment as EEnvironment } from '../../config/env-config';
import intersection from 'lodash/intersection';
import { IUserRole, EOrgAccess, EAppAccess } from '../../domains/identity';
import { QuickActionSchema } from './types/IQuickActionSchema';
import {
  ITokenClaims,
  TokenCustomClaimKeysEnum,
  fetchLastSuccessfulLoginOrganizationId,
} from '../../domains/auth0';
import { getReplacementValues } from '../utils';

const ajv = new Ajv({ useDefaults: true });

class QuickAction {
  private readonly _jsonData: QuickActionSchema;
  schemaErrors: ErrorObject[] | null | undefined = [];
  runtimeError: Error | undefined | null = null;
  public isValid = false;

  private readonly resources: {
    tenant?: ITenant;
    activeProducts: string[];
    userRoles: IUserRole[];
    tokenClaims?: ITokenClaims;
    _self: QuickActionSchema;
  };

  public readonly metadata: {
    analyticsId?: string;
    dataUrl?: string;
  };

  private readonly hostCta?: (
    url: string | LocationDescriptor<unknown> | undefined,
  ) => void;

  private readonly env: EEnvironment;

  public viewModel: IViewModel = {
    title: '',
    description: '',
    category: '',
    icon: {},
    color: '', // should be a chakra color string, please see chakra docs
    ctaCallback: noop,
    type: '',
    productCode: '',
    tenantId: '',
  };
  // Environment and access flags
  private envDisabled = false;
  private orgAccessDisabled = false;
  private appAccessDisabled = false;
  private productAccessDisabled = false;
  private isEnabled: boolean;
  public isDisabled = false;

  constructor(
    {
      jsonData,
      tenant,
      userRoles = [],
      hostCta,
      env,
      tokenClaims,
      activeProducts = [],
    }: IQuickActionInput,
    isEnabled = true,
  ) {
    this.env = EEnvironment[env as keyof typeof EEnvironment];
    this._jsonData = jsonData;
    this.isEnabled = isEnabled;

    this.resources = {
      tenant,
      userRoles,
      activeProducts,
      tokenClaims: this.getHotFixTokenClaims(tokenClaims),
      _self: jsonData,
    };

    this.metadata = {};

    this._validateSchema();

    if (!this.isValid) return;

    const isExternalLink = this._jsonData.link.type === 'external-link';
    this.metadata = {
      analyticsId: jsonData['analytics-id'],
      ...(isExternalLink && { dataUrl: this.getCtaLink() }),
    };
    this.hostCta = hostCta;
    this.init();
  }

  init() {
    this._setEnvironmentDisabled();
    this._setOrgAccessDisabled();
    this._setAppAccessDisabled();
    this._setProductAccessDisabled();
    /**
     enabled = orgAccess || appAccess
     disabled = !orgAccess && !appAccess
    };
     */

    const accessIsEnabled = () => {
      const orgAccessEnabled = !this.orgAccessDisabled;
      const appAccessEnabled = !this.appAccessDisabled;

      return orgAccessEnabled || appAccessEnabled;
    };

    const accessDisabled =
      this._jsonData.type === 'ui'
        ? this.orgAccessDisabled
        : !accessIsEnabled();

    this.isDisabled =
      !this.isEnabled ||
      this.envDisabled ||
      accessDisabled ||
      this.productAccessDisabled;
    this._toViewModel();
  }

  /**
   * It is used as a hotfix for organization id resolution, considering the adr implementation
   * ticket PORTAL-1691
   * @param tokenClaims
   * @private
   */
  private getHotFixTokenClaims(tokenClaims: any) {
    if (!tokenClaims) return;
    const params = new URLSearchParams(window.location.search);
    const organization = params.get('organization');

    const organizationId =
      organization ||
      fetchLastSuccessfulLoginOrganizationId() ||
      tokenClaims[TokenCustomClaimKeysEnum.ORG_ID] ||
      '';

    return {
      ...tokenClaims,
      [TokenCustomClaimKeysEnum.ORG_ID]: organizationId,
    };
  }

  private _validateSchema() {
    try {
      const validate = ajv.compile(jsonSchema);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!this._jsonData)
        throw new Error('jsonSchema cannot be null or undefined');

      if (validate(this._jsonData)) {
        this.isValid = true;
      } else {
        this.schemaErrors = validate.errors;
        this.isValid = false;
      }
    } catch (err) {
      this.isValid = false;
      this.runtimeError = err as Error;
    }
  }

  private _setEnvironmentDisabled() {
    if (!this._jsonData.envAccess) return;

    if (!this._jsonData.envAccess.includes(this.env)) {
      this.envDisabled = true;
    }
  }

  private _setOrgAccessDisabled() {
    if (!this._jsonData.orgAccess || !this.resources.userRoles) return;

    const { organizationRole } = generateUserRoleData(this.resources.userRoles);

    const userHasOrganizationAccess = this._jsonData.orgAccess.includes(
      organizationRole as EOrgAccess,
    );

    this.orgAccessDisabled = !userHasOrganizationAccess;
  }

  private _setAppAccessDisabled() {
    // [!IMPORTANT, !ALERT, !IMPROVE IT]
    // If there is no appAccess property, it means that the app is accessible to all users
    // Therefor we disable the app access in order to work with the condition in line 119
    if (!this._jsonData.appAccess) {
      this.appAccessDisabled = true;
      return;
    }

    const { appRoles } = generateUserRoleData(this.resources.userRoles);

    const tenantId = this.resources.tenant?.id;
    const requiredRoles = this._jsonData.appAccess;

    this.appAccessDisabled = !(
      tenantId && requiredRoles.includes(appRoles[tenantId] as EAppAccess)
    );
  }

  private _setProductAccessDisabled() {
    if (!this._jsonData.productAccess) return;

    this.productAccessDisabled =
      intersection(this._jsonData.productAccess, this.resources.activeProducts)
        .length === 0;
  }

  // It is used to resolve all the view values to the view model for ui consumption
  private _toViewModel() {
    this.viewModel.category = this._jsonData.category ?? '';
    this.viewModel.title = this._jsonData.name;
    this.viewModel.description = this._getDescription();
    // It is used for call to action like `onClick` etc.
    this.viewModel.ctaCallback = this._getCtaCallback();
    this.viewModel.color = this._getIconColor();
    this.viewModel.type = this._jsonData.type;
    this._setIconDetailsToViewModel();
    this.viewModel.type = this._jsonData.type;
    this.viewModel.tenantId = this.resources.tenant?.id ?? '';
  }

  private _setIconDetailsToViewModel() {
    const { type, src } = this._jsonData.icon;
    this.viewModel.icon = {};

    switch (type) {
      case 'url':
        this.viewModel.icon.iconUrl = src;
        break;
      case 'mdi':
        this.viewModel.icon.mdiIcon = src;
        break;
      case 'blok':
        this.viewModel.icon.blokIcon = src;
        break;
      default:
        break;
    }
  }

  private _getDescription() {
    if (!this._jsonData.displayName || !this._jsonData.displayName.name) return;

    const replacements = this._jsonData.displayName?.replacements;
    const displayName = this._jsonData.displayName?.name;

    if (!replacements || !replacements.length) return displayName;

    const { resultString: subTitle } = getReplacementValues({
      replacements,
      jsonPath: displayName,
      resources: this.resources,
      options: { env: this.env, tokenClaims: this.resources.tokenClaims },
    });

    return subTitle.replace(/\{(.*?)\}/g, '');
  }

  public getCtaLink() {
    const link = this._jsonData.link.to;
    const replacements = this._jsonData.link?.replacements;

    if (!replacements) return link;

    const { resultString: finalUrl } = getReplacementValues({
      replacements,
      jsonPath: link,
      resources: this.resources,
      options: { env: this.env, tokenClaims: this.resources.tokenClaims },
    });

    return finalUrl;
  }

  private _getCtaCallback() {
    try {
      // Resolve link url based on json data
      const ctaUrl = this.getCtaLink();

      const callback = (() => {
        // Resolve the callback for external link
        if (this._jsonData.link.type === 'external-link') {
          return redirect;
        }

        // Resolve the callback with a user's defined call to action (this.hostCta) function
        if (
          this._jsonData.link.type === 'internal-link' &&
          this.hostCta &&
          typeof this.hostCta === 'function'
        ) {
          return (url = ctaUrl) => this.hostCta && this.hostCta(url);
        }

        if (this._jsonData.link.type === 'internal-state' && this.hostCta) {
          const dispatch = this.hostCta;
          const payload = this._jsonData.link.to;
          return () => dispatch(payload);
        }

        return noop;
      })();

      return () => callback(ctaUrl);
    } catch {
      // fail with a no operation function
      return () => noop();
    }
  }

  private _getIconColor() {
    return this._jsonData.color;
  }
}

export { QuickAction };
